import { CustomFieldRecord, CustomField } from '@/models/types/custom-field'

export function useCustomFieldPathParser() {
  function parseCustomDataPath(path: string): Array<string | number> {
    const withoutPrefix = path.startsWith('custom_data.')
      ? path.substring('custom_data.'.length)
      : path

    const segments: Array<string | number> = []
    let currentSegment = ''
    let i = 0

    while (i < withoutPrefix.length) {
      const char = withoutPrefix[i]

      if (char === '[') {
        // Push the current segment if it exists
        if (currentSegment) {
          segments.push(currentSegment)
          currentSegment = ''
        }
        // Find the closing bracket and parse the index
        const closingBracket = withoutPrefix.indexOf(']', i)
        if (closingBracket !== -1) {
          const indexStr = withoutPrefix.substring(i + 1, closingBracket)
          const index = parseInt(indexStr, 10)
          if (!isNaN(index)) {
            segments.push(index)
          }
          i = closingBracket + 1
        } else {
          i++
        }
      } else if (char === '.') {
        // Push the current segment and start a new one
        if (currentSegment) {
          segments.push(currentSegment)
          currentSegment = ''
        }
        i++
      } else {
        currentSegment += char
        i++
      }
    }

    // Push the last segment if it exists
    if (currentSegment) {
      segments.push(currentSegment)
    }

    return segments
  }

  function getCustomFieldLabel(
    path: string,
    customFieldDefinitions?: CustomFieldRecord,
  ): string | null {
    if (!customFieldDefinitions) {
      // Fallback: return the last segment of the path
      const segments = parseCustomDataPath(path)
      return segments.length > 0 ? String(segments[segments.length - 1]) : path
    }

    const segments = parseCustomDataPath(path)
    if (segments.length === 0) {
      return path
    }

    const labels: string[] = []
    let currentField: CustomField | undefined
    let currentDefinitions = customFieldDefinitions

    // Helper to convert children to CustomFieldRecord
    const getChildrenAsRecord = (
      children: CustomField[] | CustomFieldRecord | null | undefined,
    ): CustomFieldRecord => {
      if (!children) {
        return {}
      }
      // If it's already a record (object), return it
      if (!Array.isArray(children)) {
        return children as CustomFieldRecord
      }
      // If it's an array, convert to record keyed by the 'key' property
      const record: CustomFieldRecord = {}
      for (const child of children) {
        if (child.key) {
          record[child.key] = child
        }
      }
      return record
    }

    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i]

      if (typeof segment === 'number') {
        // Array index - use the parent field's title with index
        if (currentField && currentField.title) {
          labels.push(`${currentField.title} #${segment + 1}`)
        } else {
          labels.push(`Item #${segment + 1}`)
        }
        // For arrays, the next segment should be a property of the array item
        // Look in the array's items.children
        if (currentField?.dataType === 'array' && currentField.items) {
          if (i + 1 < segments.length) {
            const nextSegment = segments[i + 1]
            if (typeof nextSegment === 'string') {
              const itemChildren = getChildrenAsRecord(currentField.items.children)
              const childField = itemChildren[nextSegment]
              if (childField) {
                currentField = childField
                currentDefinitions = getChildrenAsRecord(childField.children)
                i++ // Skip the next segment since we've processed it
                if (childField.title) {
                  labels.push(childField.title)
                } else {
                  labels.push(nextSegment)
                }
              } else {
                // Field not found in schema, use the segment name
                labels.push(nextSegment)
                currentDefinitions = {}
                i++ // Skip the next segment
              }
            }
          }
        } else {
          // Not an array or no items definition, can't navigate further
          currentDefinitions = {}
        }
      } else {
        // Property name
        currentField = currentDefinitions?.[segment]
        if (currentField) {
          if (currentField.title) {
            labels.push(currentField.title)
          } else {
            labels.push(segment)
          }
          // Update currentDefinitions for nested navigation
          if (currentField.children) {
            currentDefinitions = getChildrenAsRecord(currentField.children)
          } else if (currentField.dataType === 'array' && currentField.items) {
            // For arrays, prepare to handle array indices
            // Keep currentField so we can use its title for the index label
            currentDefinitions = getChildrenAsRecord(currentField.items.children)
          } else {
            currentDefinitions = {}
          }
        } else {
          return null
        }
      }
    }

    return labels.join(' · ')
  }

  function getCustomFieldDefinition(
    path: string,
    customFieldDefinitions?: CustomFieldRecord,
  ): CustomField | undefined {
    if (!customFieldDefinitions) {
      return undefined
    }

    const segments = parseCustomDataPath(path)
    if (segments.length === 0) {
      return undefined
    }

    let currentField: CustomField | undefined
    let currentDefinitions = customFieldDefinitions

    // Helper to convert children to CustomFieldRecord
    const getChildrenAsRecord = (
      children: CustomField[] | CustomFieldRecord | null | undefined,
    ): CustomFieldRecord => {
      if (!children) {
        return {}
      }
      if (!Array.isArray(children)) {
        return children as CustomFieldRecord
      }
      const record: CustomFieldRecord = {}
      for (const child of children) {
        if (child.key) {
          record[child.key] = child
        }
      }
      return record
    }

    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i]

      if (typeof segment === 'number') {
        // Array index - navigate to the item's children
        if (currentField?.dataType === 'array' && currentField.items) {
          if (i + 1 < segments.length) {
            const nextSegment = segments[i + 1]
            if (typeof nextSegment === 'string') {
              const itemChildren = getChildrenAsRecord(currentField.items.children)
              currentField = itemChildren[nextSegment]
              if (currentField) {
                currentDefinitions = getChildrenAsRecord(currentField.children)
                i++ // Skip the next segment
              } else {
                return undefined
              }
            }
          } else {
            // Last segment is the array index itself - return the array field
            return currentField
          }
        } else {
          return undefined
        }
      } else {
        // Property name
        currentField = currentDefinitions?.[segment]
        if (currentField) {
          if (currentField.children) {
            currentDefinitions = getChildrenAsRecord(currentField.children)
          } else if (currentField.dataType === 'array' && currentField.items) {
            currentDefinitions = getChildrenAsRecord(currentField.items.children)
          } else {
            currentDefinitions = {}
          }
        } else {
          return undefined
        }
      }
    }

    return currentField
  }

  return {
    parseCustomDataPath,
    getCustomFieldLabel,
    getCustomFieldDefinition,
  }
}
